import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  onAuthChange,
  ensureSignedIn,
  signIn as fbSignIn,
  signUp as fbSignUp,
  signOut as fbSignOut,
  signInWithGoogle as fbSignInWithGoogle,
  type FirebaseUser,
} from '../services/auth';
import { fetchUser, createUserDoc, setUserCars } from '../services/users';
import { setLoggerUserId, logWarn } from '../lib/logger';
import type { User, Car } from '../types';

export const DEFAULT_CAR: Car = {
  id: 'default',
  label: 'Mercedes G63',
  type: 'suv',
  heightM: 1.95,
};

interface AuthContextValue {
  firebaseUser: FirebaseUser | null;
  profile: User | null;
  ready: boolean;
  /** True once `ready`, for every session — anonymous auth guarantees a uid from mount. */
  isAnonymous: boolean;
  effectiveCars: Car[];
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  saveCars: (cars: Car[]) => Promise<void>;
  reloadProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<User | null>(null);
  const [ready, setReady] = useState(false);

  const loadProfile = useCallback(async (u: FirebaseUser) => {
    let p = await fetchUser(u.uid);
    if (!p) {
      p = await createUserDoc(u.uid, u.email, u.displayName);
    }
    setProfile(p);
  }, []);

  useEffect(() => {
    const unsub = onAuthChange(async (u) => {
      if (!u) {
        // No session at all (first load, or just signed out and about to
        // re-anonymize) — bootstrap an anonymous session so the app always
        // has a uid to write a garage against. onAuthChange will fire again
        // once that resolves.
        setLoggerUserId(null);
        try {
          await ensureSignedIn();
        } catch (err) {
          logWarn('anonymous sign-in failed', { error: err instanceof Error ? err.message : String(err) });
          setFirebaseUser(null);
          setProfile(null);
          setReady(true);
        }
        return;
      }
      setFirebaseUser(u);
      setLoggerUserId(u.uid);
      try {
        await loadProfile(u);
      } catch (err) {
        logWarn('loadProfile failed', { error: err instanceof Error ? err.message : String(err) });
        setProfile(null);
      }
      setReady(true);
    });
    return unsub;
  }, [loadProfile]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const u = await fbSignIn(email, password);
      await loadProfile(u);
    },
    [loadProfile]
  );

  const signUp = useCallback(
    async (email: string, password: string) => {
      const u = await fbSignUp(email, password);
      await loadProfile(u);
    },
    [loadProfile]
  );

  const signInWithGoogle = useCallback(async () => {
    const cred = await fbSignInWithGoogle();
    await loadProfile(cred.user);
  }, [loadProfile]);

  const signOut = useCallback(async () => {
    await fbSignOut();
    setLoggerUserId(null);
    setProfile(null);
  }, []);

  const saveCars = useCallback(
    async (cars: Car[]) => {
      if (!firebaseUser) throw new Error('Sign in to save your garage.');
      await setUserCars(firebaseUser.uid, cars);
      setProfile((p) => (p ? { ...p, cars } : p));
    },
    [firebaseUser]
  );

  const reloadProfile = useCallback(async () => {
    if (!firebaseUser) return;
    await loadProfile(firebaseUser);
  }, [firebaseUser, loadProfile]);

  const effectiveCars = profile?.cars && profile.cars.length > 0 ? profile.cars : [DEFAULT_CAR];
  const isAnonymous = firebaseUser?.isAnonymous ?? false;

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        profile,
        ready,
        isAnonymous,
        effectiveCars,
        signIn,
        signUp,
        signInWithGoogle,
        signOut,
        saveCars,
        reloadProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
